/**
 * Coup de pouce — Attendu 1057 « écrire un même nombre sous de multiples
 * formes : 1,2 = 12/10 = 6/5 = 1 + 1/5 = 120 % = 120/100 ». DEUX pages
 * STATIQUES, partagées par les trois generator_keys, puces « Virgule ou
 * pourcentage » et « Fraction ». Phrases relues par l'auteur à l'épreuve de
 * Musk (le prof : « corrige toi-même », 2026-09-09), codées mot pour mot.
 *
 * Un seul principe : POUR CENT, C'EST SUR 100. L'image que l'élève a déjà :
 * la grille de 100 de 634 et de 1056. Deux grilles côte à côte : une pleine
 * (1, ou 5 parts sur 5) et une aux 20 premières cases orange (0,2, ou une
 * part sur 5) ; en gros le calcul, 1,2 = 120/100 = 120 % (page 1) et
 * 6/5 = 120/100 = 120 % avec les flèches × 20 de 1051 (page 2) ; puis UNE
 * ligne du geste, en corps 15 :
 *   page 1 : « 1,2 : 100 cases et 20 cases, 120 sur 100. »
 *   page 2 : « 1/5, c'est 20 cases : 6 parts, 120 cases. »
 * Corrections de l'auteur sur le texte nu : « 1,2, c'est 120 cases sur
 * 100 » (deux virgules à la suite, illisible) lit maintenant les deux
 * grilles ; « une part sur 5, c'est 20 cases » ne menait pas à 120, la ligne
 * compte les 6 parts. Pas de vérification, pas de ligne de reconnaissance.
 * Rejetée : la droite graduée (en centièmes illisible, en dixièmes un pas de
 * plus).
 *
 * SACRIFIÉ : 1,2 sous toutes ses formes (1,2 ; 120 % ; 120/100 ; 12/10 ;
 * 6/5 ; 1 + 1/5), jamais tiré, même comme distracteur (gen_1057, auto-testé
 * par cdp1057.self.test.js). Largeur bornée à 340 px, tailles de 1048.
 */

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';
const GRIS = '#94A3B8';

/** Deux grilles 10 × 10 de cases de 12 px : la pleine à gauche (40 → 160), la partielle à droite (240 → 360). */
const CASE = 12;
const Y0 = 44;
const XG = 40;
const XD = 240;

/** Une grille, les cases où `prise(ligne, colonne)` en orange ; `bandes` trace un trait fort toutes les deux lignes (une bande = 20 cases = 1/5). */
const grille = (x0, prise, bandes = false) => {
    const cases = Array.from({ length: 100 }, (_, k) => {
        const l = Math.floor(k / 10);
        const c = k % 10;
        const orange = prise(l, c);
        return `<rect x="${x0 + c * CASE}" y="${Y0 + l * CASE}" width="${CASE}" height="${CASE}" fill="${orange ? ORANGE : '#FFFFFF'}" stroke="${orange ? '#FFFFFF' : GRIS}" stroke-width="0.6"/>`;
    }).join('\n  ');
    const traits = bandes
        ? [2, 4, 6, 8].map((l) => `<line x1="${x0}" y1="${Y0 + l * CASE}" x2="${x0 + 10 * CASE}" y2="${Y0 + l * CASE}" stroke="${BLEU}" stroke-width="2"/>`).join('\n  ')
        : '';
    return `${cases}
  ${traits}
  <rect x="${x0}" y="${Y0}" width="${10 * CASE}" height="${10 * CASE}" fill="none" stroke="${ENCRE}" stroke-width="1.4"/>`;
};

/** Une fraction empilée, une couleur. */
const frac = (x, y, n, d, couleur, taille = 15, demi = 12) => `<text x="${x}" y="${y - 4}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${n}</text>
  <line x1="${x - demi}" y1="${y}" x2="${x + demi}" y2="${y}" stroke="${couleur}" stroke-width="1.8"/>
  <text x="${x}" y="${y + taille + 1}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${d}</text>`;

const op = (x, y, s) => `<text x="${x}" y="${y}" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}">${s}</text>`;

const svg = (aria, corps) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 296" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="${aria}">
  ${corps}
</svg>`.trim();

// ─── Page 1 : Virgule ou pourcentage, 1,2 = 120/100 = 120 % ───────────────

const SVG_VIRGULE = svg(
    'Deux grilles de 100 cases. À gauche, la grille entièrement orange, étiquetée 1 égale 100 %. À droite, les 20 premières cases en orange, étiquetée 0,2 égale 20 %. 1,2 égale 120 sur 100 égale 120 %. 1,2 : 100 cases et 20 cases, 120 sur 100.',
    `<!-- 1. les deux grilles : 1 = 100 % et 0,2 = 20 % -->
  <text x="${XG + 5 * CASE}" y="30" font-size="14" font-weight="bold" text-anchor="middle" fill="${ENCRE}">1 = 100 %</text>
  ${grille(XG, () => true)}
  <text x="${XD + 5 * CASE}" y="30" font-size="14" font-weight="bold" text-anchor="middle" fill="${ENCRE}">0,2 = 20 %</text>
  ${grille(XD, (l) => l < 2)}
  ${op(200, Y0 + 5 * CASE + 6, '+')}

  <!-- 2. le calcul -->
  <text x="98" y="219" font-size="16" font-weight="bold" text-anchor="middle" fill="${ORANGE}">1,2</text>
  ${op(128, 219, '=')}
  ${frac(162, 214, 120, 100, ENCRE, 15, 16)}
  ${op(196, 219, '=')}
  <text x="236" y="219" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}">120 %</text>

  <!-- 3. le geste -->
  <text x="200" y="272" font-size="15" text-anchor="middle" fill="${ENCRE}">1,2 : 100 cases et 20 cases, 120 sur 100.</text>`,
);

// ─── Page 2 : Fraction, 6/5 = 120/100 = 120 % ─────────────────────────────

const SVG_FRACTION = svg(
    'Deux grilles de 100 cases partagées en 5 bandes de 20 cases. À gauche, la grille entièrement orange, étiquetée 5 sur 5. À droite, une bande de 20 cases en orange, étiquetée 1 sur 5. 6 sur 5 égale 120 sur 100 égale 120 %, avec les flèches fois 20 en haut et en bas. 1 sur 5, c\'est 20 cases : 6 parts, 120 cases.',
    `<!-- 1. les deux grilles en 5 bandes : 5/5 et 1/5 -->
  ${frac(XG + 5 * CASE, 24, 5, 5, ENCRE, 12, 8)}
  ${grille(XG, () => true, true)}
  ${frac(XD + 5 * CASE, 24, 1, 5, ENCRE, 12, 8)}
  ${grille(XD, (l) => l < 2, true)}
  ${op(200, Y0 + 5 * CASE + 6, '+')}

  <!-- 2. le calcul, avec les flèches × 20 -->
  ${frac(92, 214, 6, 5, ORANGE, 15, 11)}
  ${op(124, 219, '=')}
  ${frac(162, 214, 120, 100, ENCRE, 15, 16)}
  <path d="M 100 190 Q 127 172 150 190" fill="none" stroke="${BLEU}" stroke-width="1.6"/>
  <polygon points="152,192 144,192 149,186" fill="${BLEU}"/>
  <text x="127" y="176" font-size="11" font-weight="bold" text-anchor="middle" fill="${BLEU}">× 20</text>
  <path d="M 100 238 Q 127 256 150 238" fill="none" stroke="${BLEU}" stroke-width="1.6"/>
  <polygon points="152,236 144,236 149,242" fill="${BLEU}"/>
  <text x="127" y="262" font-size="11" font-weight="bold" text-anchor="middle" fill="${BLEU}">× 20</text>
  ${op(196, 219, '=')}
  <text x="236" y="219" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}">120 %</text>

  <!-- 3. le geste -->
  <text x="200" y="288" font-size="15" text-anchor="middle" fill="${ENCRE}">1/5, c'est 20 cases : 6 parts, 120 cases.</text>`,
);

const AIDE = {
    pages: [
        { titre: 'Virgule ou pourcentage', svg: SVG_VIRGULE },
        { titre: 'Fraction', svg: SVG_FRACTION },
    ],
};

const CP_1057 = {
    flash_texte_trou_saisie: AIDE,
    meme_qcm_unique: AIDE,
    meme_tableau_vf: AIDE,
};

export default CP_1057;
